/**
 * THE canvas <-> image-pixel boundary. There is exactly one, and it is this file.
 *
 * Everything above this module — component state, the undo stack, autosave payloads — is in
 * **image pixel space**: origin top-left, `x` right, `y` down, `w`/`h` strictly positive,
 * **floats**. The backend stores `Double` and a box drawn on a zoomed canvas carries sub-pixel
 * information; rounding it at write time throws that away permanently. Nothing here rounds.
 *
 * Everything below this module is screen/stage space in CSS pixels.
 *
 * The transform is a uniform scale, then a rotation, then a translation:
 *
 *     stage = position + R(theta) * (scale * image)
 *     image = R(-theta) * (stage - position) / scale
 *
 * That is *exactly* what Konva applies when a Stage is given `scale({x: s, y: s})`,
 * `rotation(theta)` and `position({x, y})`, which is why the boxes layer can be drawn in image
 * coordinates directly and a dragged node's `x()`/`y()` come back already in image space. The
 * `Viewport` here is the single source of truth for that stage transform — see
 * `ImageCanvas.svelte`, which only ever copies these numbers onto the stage and never computes
 * its own.
 *
 * At `rotation: 0` every function below reduces to the arithmetic it had before rotation
 * existed, term for term. That is not a coincidence to be grateful for — it is the regression
 * net: every test written against the unrotated boundary still pins the same numbers.
 *
 * Uniform scale is deliberate: separate x and y scales would make an aspect-ratio bug
 * invisible on a square test image. Rotation is *not* anisotropic — it is rigid, so a circle
 * stays a circle and a box keeps its side lengths — which is the reason it can be admitted
 * here when a second scale factor still cannot.
 *
 * ---------------------------------------------------------------------------
 * Why the view rotates at all
 * ---------------------------------------------------------------------------
 * Most inscription photographs are not square-on. Straightening the *view* and drawing an
 * upright box is the same act as drawing a rotated box on the unrotated photograph — the
 * storage contract is image-pixel space, so the box that gets stored is oriented either way.
 * Putting the rotation in the viewport rather than in the drawing gesture is what lets the
 * annotator judge tightness against a level glyph, which is the whole ergonomic point.
 *
 * The viewport's rotation is a **view preference**, not data. It is never stored in the
 * database and never sent to the API. What is stored is each box's own `angle`.
 *
 * ---------------------------------------------------------------------------
 * Exports that are tested but not (yet) wired into a live path
 * ---------------------------------------------------------------------------
 * Stated explicitly so the test count does not imply coverage of the running app:
 * `toStagePoint`, `toStageRect`, `toImageRect`, `containsPoint`, `iou`, `rectArea` and
 * `setScaleAbout` are **not called anywhere in the SPA**. They are kept because each is the
 * exact inverse or companion of a function that *is* live (`toImagePoint`, `rectFromPoints`,
 * `zoomViewport`), and the round-trip assertions over the pairs are what make the boundary
 * auditable — a one-directional transform cannot be tested for a transpose against itself.
 * If one of them is still unused when the auto-labelling loop lands, delete it rather than
 * letting a dead utility accumulate tests.
 *
 * Live callers, for the avoidance of doubt: `toImagePoint`, `rectFromPoints`, `isDrawable`,
 * `fitViewport`, `zoomViewport`, `panViewport`, `rotateViewport`, `clampRectToImage`,
 * `orientedFromDrag`, `orientedFromNode`, `cornersOf`, `envelopeOf`, `normalizeAngle`.
 */

export interface Point {
  x: number;
  y: number;
}

/** An axis-aligned rectangle. Which space it is in is stated by the variable that holds it. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A rectangle that may be turned.
 *
 * `x`/`y`/`w`/`h` describe it **at angle zero**; `angle` then rotates it about its own centre,
 * `(x + w/2, y + h/2)`. Degrees, clockwise-positive — the natural sense where `y` runs down —
 * normalized to `(-180, 180]`.
 *
 * An `OrientedRect` with `angle: 0` is exactly a `Rect` plus a zero, which is why the two
 * coexist here instead of one replacing the other: the axis-aligned helpers stay honest about
 * only handling the axis-aligned case rather than quietly ignoring an angle.
 *
 * `kind` mirrors the server's `geometry_kind` and is deliberately not derivable from `angle`:
 * `'obb'` at angle zero records a box drawn on a rotatable canvas that came out upright, which
 * is a different statement from a box drawn before the canvas could rotate.
 */
export interface OrientedRect extends Rect {
  angle: number;
  kind: GeometryKind;
}

export type GeometryKind = 'aabb' | 'obb';

/** Fold an angle into `(-180, 180]`, so one orientation has exactly one spelling. */
export function normalizeAngle(degrees: number): number {
  if (!Number.isFinite(degrees)) {
    throw new Error(`coords: angle must be finite, got ${degrees}`);
  }
  let value = degrees % 360;
  if (value > 180) value -= 360;
  else if (value <= -180) value += 360;
  // `-0` renders as "-0" in JSON and compares !== when a test stringifies a payload.
  return value === 0 ? 0 : value;
}

/** An upright rect, tagged as drawn before rotation was available. */
export function rectToOriented(r: Rect, kind: GeometryKind = 'aabb'): OrientedRect {
  return { ...r, angle: 0, kind };
}

function rotatePoint(p: Point, degrees: number): Point {
  if (degrees === 0) return { x: p.x, y: p.y };
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos };
}

/** The centre of a rect, in whatever space it is in. */
export function centerOf(r: Rect): Point {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/**
 * The four corners, clockwise from the box's **own** top-left.
 *
 * "Top-left" is in the box's local frame, so for a turned box the first corner is not
 * necessarily the topmost on screen. That is deliberate — the order is what carries the
 * orientation through to the export's polygon.
 */
export function cornersOf(o: OrientedRect): Point[] {
  const centre = centerOf(o);
  const half = { x: o.w / 2, y: o.h / 2 };
  return (
    [
      { x: -half.x, y: -half.y },
      { x: half.x, y: -half.y },
      { x: half.x, y: half.y },
      { x: -half.x, y: half.y },
    ] as Point[]
  ).map((local) => {
    const turned = rotatePoint(local, o.angle);
    return { x: centre.x + turned.x, y: centre.y + turned.y };
  });
}

/**
 * The smallest upright rect containing an oriented one.
 *
 * A lossy view, never a substitute: for a 45-degree glyph this is half again as large as the
 * box the annotator drew. It exists for hit-testing and for the exporters' compatibility
 * output, both of which say so where they use it.
 */
export function envelopeOf(o: OrientedRect): Rect {
  if (o.angle === 0) return { x: o.x, y: o.y, w: o.w, h: o.h };
  const corners = cornersOf(o);
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return { x: left, y: top, w: Math.max(...xs) - left, h: Math.max(...ys) - top };
}

export interface Size {
  width: number;
  height: number;
}

/**
 * The stage transform: image-pixel -> stage-pixel is `{x, y} + R(rotation) * (p * scale)`.
 *
 * `rotation` is required rather than optional, and every existing call site was updated to
 * pass `0`. An optional field would have let a viewport constructed somewhere new silently
 * mean "unrotated" when the surrounding screen was rotated, and the symptom of that is boxes
 * stored at the wrong angle — which nothing on screen would show.
 */
export interface Viewport {
  scale: number;
  x: number;
  y: number;
  /** Degrees clockwise. A view preference; never stored, never sent to the API. */
  rotation: number;
}

export const IDENTITY_VIEWPORT: Viewport = { scale: 1, x: 0, y: 0, rotation: 0 };

export const MIN_SCALE = 0.02;
export const MAX_SCALE = 60;

/** Smallest box we will accept from a drag, in image pixels. Below this it is a stray click. */
export const MIN_BOX_PIXELS = 2;

function assertFinite(value: number, what: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`coords: ${what} must be finite, got ${value}`);
  }
}

function assertViewport(v: Viewport): void {
  assertFinite(v.scale, 'viewport.scale');
  assertFinite(v.x, 'viewport.x');
  assertFinite(v.y, 'viewport.y');
  assertFinite(v.rotation, 'viewport.rotation');
  if (v.scale <= 0) {
    throw new Error(`coords: viewport.scale must be positive, got ${v.scale}`);
  }
}

// ---------------------------------------------------------------------------
// Points
// ---------------------------------------------------------------------------

/** Image pixel -> stage pixel. */
export function toStagePoint(v: Viewport, p: Point): Point {
  assertViewport(v);
  const scaled = rotatePoint({ x: p.x * v.scale, y: p.y * v.scale }, v.rotation);
  return { x: scaled.x + v.x, y: scaled.y + v.y };
}

/** Stage pixel -> image pixel. The exact inverse of {@link toStagePoint}. */
export function toImagePoint(v: Viewport, p: Point): Point {
  assertViewport(v);
  const unturned = rotatePoint({ x: p.x - v.x, y: p.y - v.y }, -v.rotation);
  return { x: unturned.x / v.scale, y: unturned.y / v.scale };
}

/**
 * The "unrotated stage" frame: stage pixels with the translation and scale removed but the
 * rotation left in. Equivalently `R(theta) * image`.
 *
 * This is the frame a drag happens in — the annotator's rectangle is axis-aligned *here*, not
 * in image space — and computing it as `(stage - position) / scale` costs no trigonometry at
 * all, which is why {@link orientedFromDrag} is built on it rather than on two `toImagePoint`
 * calls and an inverse rotation.
 */
function toViewFrame(v: Viewport, p: Point): Point {
  return { x: (p.x - v.x) / v.scale, y: (p.y - v.y) / v.scale };
}

// ---------------------------------------------------------------------------
// Rectangles
// ---------------------------------------------------------------------------

/** Image-space rect -> stage-space rect. */
export function toStageRect(v: Viewport, r: Rect): Rect {
  assertViewport(v);
  return { x: r.x * v.scale + v.x, y: r.y * v.scale + v.y, w: r.w * v.scale, h: r.h * v.scale };
}

/** Stage-space rect -> image-space rect. The exact inverse of {@link toStageRect}. */
export function toImageRect(v: Viewport, r: Rect): Rect {
  assertViewport(v);
  return { x: (r.x - v.x) / v.scale, y: (r.y - v.y) / v.scale, w: r.w / v.scale, h: r.h / v.scale };
}

/**
 * The rectangle spanned by two corners, in whatever space they share.
 *
 * A drag can start at any corner, so `w`/`h` are normalized positive here rather than left for
 * a consumer to fix up — a negative width reaching the API is rejected by `BBoxModel`, and a
 * negative width reaching the *database* would be a box nothing can render.
 */
export function rectFromPoints(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

/**
 * The box an annotator drew by dragging, expressed in image space.
 *
 * The rectangle is axis-aligned **in the view**, not in the image: that is the whole point of
 * straightening the photograph before boxing. So the corners are taken into the unrotated-stage
 * frame (where the drag *is* axis-aligned, and which costs no trigonometry), spanned there with
 * the same {@link rectFromPoints} that has always normalized a backwards drag, and the centre is
 * turned back into image space.
 *
 * The resulting angle is `-viewport.rotation`, and the sign is worth checking rather than
 * trusting: a screen-horizontal step maps to `R(-theta) * (1, 0)` in image space, so the box's
 * own x-axis lies at `-theta` there. Getting this backwards produces boxes that look perfect
 * while the view is turned and sit at twice the wrong angle when it is not — which is exactly
 * what step 3 of the manual check in the plan is for.
 */
export function orientedFromDrag(v: Viewport, aStage: Point, bStage: Point): OrientedRect {
  assertViewport(v);
  const local = rectFromPoints(toViewFrame(v, aStage), toViewFrame(v, bStage));
  const centre = rotatePoint(centerOf(local), -v.rotation);
  const angle = normalizeAngle(-v.rotation);
  return {
    x: centre.x - local.w / 2,
    y: centre.y - local.h / 2,
    w: local.w,
    h: local.h,
    angle,
    // 'obb' even at angle zero: the canvas could rotate, so an upright box here is a
    // judgement rather than a limitation. That distinction is the only way to find the boxes
    // drawn before this existed.
    kind: 'obb',
  };
}

/**
 * The subset of a Konva node's geometry that describes an axis-aligned box.
 *
 * Duck-typed rather than importing Konva so the handoff can be tested without a canvas: this
 * is exactly `{x(), y(), width(), height(), scaleX(), scaleY(), rotation()}` read off a
 * `Konva.Rect`.
 *
 * `offsetX`/`offsetY` are here because box nodes now carry one. The previous revision of this
 * comment said they were absent by assumption and instructed that they "be added (and
 * subtracted) at the same commit that introduces the offset, not afterwards" — this is that
 * commit. A box node is positioned at its **centre** with an offset of half its size, because
 * that is what makes Konva's `Transformer` turn it about its centre rather than a corner;
 * without the fields below, `x()`/`y()` would be read as a top-left corner and every stored
 * rectangle would shift by half its size, silently.
 */
export interface NodeBox {
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  /** Degrees, as Konva reports it. */
  rotation: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Konva node -> stored rectangle. **The one conversion that was silently corrupting boxes.**
 *
 * Konva expresses a resize as a *scale* on the node, and a `Transformer` with the default
 * `flipEnabled: true` expresses a handle dragged past the opposite edge as a **negative**
 * scale. Taking the size without `Math.abs()` would store a negative width — a box nothing can
 * render and the API rejects. The flip is disabled on the Transformer as well, but this
 * function is the backstop, because "the config still says flipEnabled: false" is not
 * something a dataset can rely on three refactors from now.
 *
 * The centre is derived from the whole transform rather than read off `x()`/`y()`, because
 * Konva applies `translate(position) . rotate . scale . translate(-offset)`, so a node's
 * position is the world location of its *offset origin* and nothing else. Working the centre
 * out this way is correct for any offset, including none — so this function cannot break the
 * next time the rendering moves where the offset sits.
 *
 * Returns `null` only for a non-finite node. It used to return `null` for a rotated one too,
 * on the grounds that "inventing an axis-aligned rectangle for it would be a lie" — quite
 * right, and the fix was to stop needing to invent one rather than to keep refusing.
 */
export function orientedFromNode(node: NodeBox): OrientedRect | null {
  const values = [
    node.x,
    node.y,
    node.width,
    node.height,
    node.scaleX,
    node.scaleY,
    node.rotation,
    node.offsetX,
    node.offsetY,
  ];
  if (values.some((value) => !Number.isFinite(value))) return null;

  const w = Math.abs(node.width * node.scaleX);
  const h = Math.abs(node.height * node.scaleY);
  const local = {
    x: (node.width / 2 - node.offsetX) * node.scaleX,
    y: (node.height / 2 - node.offsetY) * node.scaleY,
  };
  const turned = rotatePoint(local, node.rotation);
  const centre = { x: node.x + turned.x, y: node.y + turned.y };
  const angle = normalizeAngle(node.rotation);
  return {
    x: centre.x - w / 2,
    y: centre.y - h / 2,
    w,
    h,
    angle,
    kind: angle === 0 ? 'aabb' : 'obb',
  };
}

export interface ClampResult {
  rect: Rect;
  /** True when the box had to be moved or shrunk to fit. Surfaced, never swallowed. */
  clamped: boolean;
  /** True when nothing survived the clamp: the box lay entirely outside the image. */
  degenerate: boolean;
}

/**
 * Clamp a box to the image bounds.
 *
 * The API clamps too and *reports* every clamp as a write anomaly, because a box outside the
 * image is usually a coordinate-space bug rather than a careless annotator. Clamping here as
 * well is not duplication of the rule — it is how the drag preview stops at the image edge —
 * but the client must never treat a clamp as routine: `clamped` is surfaced in the UI.
 *
 * **Upright boxes only.** A turned box has no well-defined trim, and the server does not
 * attempt one either; use {@link cornersOutside} for those.
 */
export function clampRectToImage(r: Rect, size: Size): ClampResult {
  const left = Math.min(Math.max(r.x, 0), size.width);
  const top = Math.min(Math.max(r.y, 0), size.height);
  const right = Math.min(Math.max(r.x + r.w, 0), size.width);
  const bottom = Math.min(Math.max(r.y + r.h, 0), size.height);
  const rect: Rect = { x: left, y: top, w: right - left, h: bottom - top };
  const clamped = rect.x !== r.x || rect.y !== r.y || rect.w !== r.w || rect.h !== r.h;
  return { rect, clamped, degenerate: rect.w <= 0 || rect.h <= 0 };
}

/**
 * How far outside the image a turned box's corner may sit before it counts as outside.
 *
 * Mirrors `CORNER_TOLERANCE_PX` on the server, and exists for the same reason: a corner is
 * computed through `cos`/`sin`, and `Math.cos(Math.PI / 2)` is 6.1e-17 rather than 0. A box laid
 * exactly along the image edge comes back at `-6e-16`, and without this it would be flagged on
 * every save. A warning that fires when nothing is wrong is a warning nobody reads.
 */
export const CORNER_TOLERANCE_PX = 1e-6;

/**
 * Does any corner of a turned box fall outside the image?
 *
 * Deliberately a question and not a fix. The service stores an out-of-bounds oriented box
 * exactly as drawn and records an anomaly, because no trim of a rotated rectangle keeps both
 * its angle and the annotator's judgement about which marks belong to the glyph. The client
 * says so rather than quietly reshaping to match a clamp that is not going to happen.
 */
export function cornersOutside(o: OrientedRect, size: Size): boolean {
  const tolerance = CORNER_TOLERANCE_PX;
  return cornersOf(o).some(
    (corner) =>
      corner.x < -tolerance ||
      corner.y < -tolerance ||
      corner.x > size.width + tolerance ||
      corner.y > size.height + tolerance,
  );
}

/** Is this box big enough to be a letter rather than a stray click? */
export function isDrawable(r: Rect): boolean {
  return r.w >= MIN_BOX_PIXELS && r.h >= MIN_BOX_PIXELS;
}

export function rectArea(r: Rect): number {
  return r.w * r.h;
}

/** Intersection-over-union, image space. Used to find the box under a click. */
export function iou(a: Rect, b: Rect): number {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.w, b.x + b.w);
  const bottom = Math.min(a.y + a.h, b.y + b.h);
  if (right <= x || bottom <= y) return 0;
  const overlap = (right - x) * (bottom - y);
  const union = rectArea(a) + rectArea(b) - overlap;
  return union > 0 ? overlap / union : 0;
}

export function containsPoint(r: Rect, p: Point): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

// ---------------------------------------------------------------------------
// Viewport construction
// ---------------------------------------------------------------------------

/**
 * The viewport that fits `image` inside `container`, centred, with `padding` stage pixels of
 * margin on every side.
 *
 * Note the asymmetry this is allowed to produce: a wide image in a tall container gets equal
 * horizontal margins and large vertical ones. Getting `x` and `y` the wrong way round here
 * would centre correctly on a square container and be visibly wrong on any other, which is
 * exactly the kind of bug the tests below pin down with non-square numbers.
 */
export function fitViewport(
  image: Size,
  container: Size,
  padding = 16,
  rotation = 0,
): Viewport {
  if (image.width <= 0 || image.height <= 0) return { ...IDENTITY_VIEWPORT, rotation };
  const availableWidth = Math.max(container.width - padding * 2, 1);
  const availableHeight = Math.max(container.height - padding * 2, 1);

  // What has to fit is the turned photograph's *envelope*, not the photograph. At 45 degrees a
  // 4:3 image needs about 1.24x the room in each direction; fitting the untuned width would
  // push two corners off screen and there would be no way to zoom out to them.
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const envelopeWidth = image.width * cos + image.height * sin;
  const envelopeHeight = image.width * sin + image.height * cos;

  const scale = clampScale(
    Math.min(availableWidth / envelopeWidth, availableHeight / envelopeHeight),
  );

  // Put the image's centre at the container's centre. Written as "the offset that maps the
  // image centre onto the container centre" rather than as a width subtraction, so it stays
  // the inverse of `toStagePoint` by construction at every rotation.
  const centre = rotatePoint(
    { x: (image.width / 2) * scale, y: (image.height / 2) * scale },
    rotation,
  );
  return {
    scale,
    x: container.width / 2 - centre.x,
    y: container.height / 2 - centre.y,
    rotation,
  };
}

export function clampScale(scale: number): number {
  return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
}

/**
 * Zoom by `factor` about a stage point, keeping the image pixel under that point fixed.
 *
 * This is what makes wheel-zoom feel anchored to the cursor. It is expressed as
 * "convert the anchor to image space, rescale, then re-derive the offset that puts that image
 * point back under the anchor" so it stays the inverse of {@link toImagePoint} by construction.
 */
export function zoomViewport(v: Viewport, anchorStage: Point, factor: number): Viewport {
  assertViewport(v);
  assertFinite(factor, 'zoom factor');
  const scale = clampScale(v.scale * factor);
  return anchoredAt(v, anchorStage, scale, v.rotation);
}

/**
 * Rotate the view about a stage point, keeping the image pixel under it fixed.
 *
 * Same derivation as {@link zoomViewport}, and deliberately the same helper: turning the
 * photograph about the cursor is what stops the glyph you are looking at from sliding off
 * screen every time you nudge the angle.
 */
export function rotateViewport(v: Viewport, anchorStage: Point, deltaDegrees: number): Viewport {
  assertViewport(v);
  assertFinite(deltaDegrees, 'rotation delta');
  return anchoredAt(v, anchorStage, v.scale, normalizeAngle(v.rotation + deltaDegrees));
}

/** Set the view's absolute rotation, about a stage point. */
export function setRotationAbout(v: Viewport, anchorStage: Point, degrees: number): Viewport {
  assertViewport(v);
  return anchoredAt(v, anchorStage, v.scale, normalizeAngle(degrees));
}

/**
 * The viewport with a new scale and/or rotation that keeps `anchorStage` over the same image
 * pixel. The one place the "re-derive the offset" trick is written down.
 */
function anchoredAt(v: Viewport, anchorStage: Point, scale: number, rotation: number): Viewport {
  const anchorImage = toImagePoint(v, anchorStage);
  const turned = rotatePoint({ x: anchorImage.x * scale, y: anchorImage.y * scale }, rotation);
  return {
    scale,
    x: anchorStage.x - turned.x,
    y: anchorStage.y - turned.y,
    rotation,
  };
}

/** Pan by a delta expressed in stage pixels. */
export function panViewport(v: Viewport, dxStage: number, dyStage: number): Viewport {
  assertViewport(v);
  return { scale: v.scale, x: v.x + dxStage, y: v.y + dyStage, rotation: v.rotation };
}

/** Zoom to an absolute scale about the centre of the container. */
export function setScaleAbout(v: Viewport, anchorStage: Point, scale: number): Viewport {
  assertViewport(v);
  return zoomViewport(v, anchorStage, clampScale(scale) / v.scale);
}

/**
 * The rotation that lays the line `a`-`b` flat on screen.
 *
 * Two clicks along a baseline is how the angle actually gets set — a number field in degrees
 * asks the annotator to estimate something they can point at instead. `a` and `b` are image
 * pixels; the result is a viewport rotation, so it is the negative of the line's own bearing.
 */
export function rotationToLevel(a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return 0;
  return normalizeAngle((-Math.atan2(dy, dx) * 180) / Math.PI);
}
